import type { MetaHTMLAttributes } from "react";

import { SITE_URL } from "@/lib/config/env-var.config";

export const SITE_NAME = "Lutely";
export const SITE_TITLE = "Own the Music You Love | Lutely";
export const SITE_DESCRIPTION =
  "Discover new music directly from artists before it hits the mainstream. Buy once, listen forever — and support artists with every purchase on Lutely.";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const SITE_LOGO = `${SITE_URL}/logo.svg`;

/** Public profiles, used as `sameAs` on the Organization entity. */
export const SOCIAL_PROFILES = [
  "https://www.youtube.com/@joinlutely",
  "https://www.instagram.com/joinlutely/",
  "https://www.tiktok.com/@joinlutely",
  "https://www.facebook.com/joinlutely/",
  "https://x.com/joinlutely",
];

/**
 * Default `robots` directive for indexable pages. The max-* values opt into
 * large image thumbnails and untruncated snippets, which Google otherwise
 * caps for sites it has no preference recorded for.
 */
const INDEXABLE_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const NOINDEX_ROBOTS = "noindex, nofollow";

export type JsonLd = Record<string, unknown>;

/**
 * TanStack's head renderer turns a `script:ld+json` entry in `meta` into a
 * `<script type="application/ld+json">` tag, but types the array as plain
 * `<meta>` attributes — so the entry has to be cast past that.
 */
function jsonLdTag(entry: JsonLd): MetaHTMLAttributes<HTMLMetaElement> {
  return {
    "script:ld+json": entry,
  } as unknown as MetaHTMLAttributes<HTMLMetaElement>;
}

/** Roughly where Google stops showing a description in a result snippet. */
const MAX_DESCRIPTION_LENGTH = 160;

function normalizeWhitespace(value?: string | null): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

/** Clamps to the snippet length, cutting on a word boundary where possible. */
function truncate(value: string): string {
  if (value.length <= MAX_DESCRIPTION_LENGTH) return value;
  const clipped = value.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > 80 ? clipped.slice(0, lastSpace) : clipped).trimEnd()}…`;
}

/** Absolute URL for an in-app path. Canonical and og:url must be absolute. */
export function absoluteUrl(path = "/"): string {
  if (!path || path === "/") return SITE_URL;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/** `/@artist-slug` — the canonical shape of a public artist profile. */
export function creatorPath(slug: string): string {
  return `/@${slug}`;
}

/** `/@artist-slug/release-slug` — the canonical shape of a public release. */
export function albumPath(creatorSlug: string, albumSlug: string): string {
  return `/@${creatorSlug}/${albumSlug}`;
}

export type SeoHeadOptions = {
  title: string;
  description?: string | null;
  /**
   * The path this page should be indexed under. Emits `<link rel="canonical">`
   * plus `og:url`. Pass the *preferred* path for pages reachable by more than
   * one URL — e.g. `/albums/{id}` points at its `/@artist/release` twin.
   *
   * Only ever set this on leaf routes: TanStack concatenates the `links` of
   * every active match, so a parent and child both declaring one would ship
   * two conflicting canonicals.
   */
  canonicalPath?: string;
  /** Falls back to the site-wide OG card. */
  image?: string | null;
  /** og:type — "website" (default), "profile", "music.album", "music.song". */
  type?: string;
  /** Keeps the page out of search results (private, stub or duplicate pages). */
  noindex?: boolean;
  jsonLd?: JsonLd | Array<JsonLd | null | undefined> | null;
};

/**
 * Builds the `head` payload for a route: title, description, Open Graph,
 * Twitter card, robots directive, canonical link and optional JSON-LD.
 *
 * Meta tags are deduplicated by name/property with the deepest match winning,
 * so a route only needs to declare what differs from the root.
 */
export function seoHead({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  noindex = false,
  jsonLd,
}: SeoHeadOptions) {
  const url =
    canonicalPath === undefined ? undefined : absoluteUrl(canonicalPath);
  const ogImage = image || DEFAULT_OG_IMAGE;
  // Descriptions are often user-written bios and release notes, which arrive
  // with line breaks and run well past what a result snippet shows.
  const summary = truncate(normalizeWhitespace(description));
  const structured = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(
    (entry): entry is JsonLd => Boolean(entry),
  );

  return {
    meta: [
      { title },
      ...(summary ? [{ name: "description", content: summary }] : []),
      { name: "robots", content: noindex ? NOINDEX_ROBOTS : INDEXABLE_ROBOTS },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:type", content: type },
      { property: "og:title", content: title },
      ...(summary ? [{ property: "og:description", content: summary }] : []),
      // No og:image:width/height here: a route that supplies its own artwork
      // has no dimensions to declare, and anything set at the root would
      // survive the override and describe the wrong file.
      { property: "og:image", content: ogImage },
      { property: "og:image:alt", content: title },
      ...(url ? [{ property: "og:url", content: url }] : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      ...(summary ? [{ name: "twitter:description", content: summary }] : []),
      { name: "twitter:image", content: ogImage },
      ...structured.map(jsonLdTag),
    ],
    // A noindex page has no canonical worth declaring, and emitting one is a
    // mixed signal that search engines resolve inconsistently.
    links: url && !noindex ? [{ rel: "canonical", href: url }] : [],
  };
}

/** Shorthand for pages that exist but must never be indexed. */
export function noindexHead(title: string) {
  return seoHead({ title, noindex: true });
}

export function organizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    sameAs: SOCIAL_PROFILES,
  };
}

export function websiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        // `query` is the parameter the marketplace actually reads — see
        // marketplaceSearchSchema in src/routes/marketplace/-search.ts.
        urlTemplate: `${SITE_URL}/marketplace/albums?query={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export type BreadcrumbEntry = { name: string; path: string };

export function breadcrumbJsonLd(entries: BreadcrumbEntry[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      item: absoluteUrl(entry.path),
    })),
  };
}
